// Page: PricingPage
// Purpose: Displays plans, handles checkout creation and redirects, manages
// premium-user redirects, and supports billing portal access from settings.
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { AlertTriangle } from 'lucide-react';
import { useAuth } from '../../../context/auth';
import { createCheckoutSession, redirectToCheckout, checkStripeConnection, initializeStripe, redirectToBillingPortal, setStripePublicKeyForUser } from '../../../services/stripeService';
import { auth } from '../../../firebase/firebase';
import { signOut as firebaseSignOut } from 'firebase/auth';
import { SERVER_URL } from '../../../api/core';
import { deleteUserAccount } from '../../../api/accounts/authApi';
import FrequencyToggle from '../../../components/auth/pricing/FrequencyToggle';
import { ButtonSpinner } from '../../../components/ui/feedback/loading-spinner.jsx';
import { PricingPlans, RoboCentSubscriptionsCard } from '../../../components/auth/pricing/PricingPlans';
import { SimpleLandingHeader } from '@/components/landing/shared/SimpleLandingHeader/SimpleLandingHeader';
import { Footer } from '../../../components/shared/Footer';
import useDarkMode from '../../../hooks/useDarkMode';
import pricingData from '../../../data/pricing/pricingPlans';
import { kevinMeetingUrl } from '../../../data/scheduling/schedulingLinks';
import AuthLoadingScreen from '../../../components/shared/AuthLoadingScreen';
import { syncSubscriptionData } from '../../../services/subscriptionSyncService';
import { trackInitiateCheckout } from '../../../utils/analytics/metaPixel';

// Function to clear all AI-related localStorage items (copied from UserDropdown)
const clearAIStorageData = () => {
  // CopyAnalyzer storage keys
  localStorage.removeItem('refrral_ad_copy');
  localStorage.removeItem('refrral_ad_analysis');
  
  // EasyWriter storage keys (if any are added in the future)
  localStorage.removeItem('refrral_easy_writer_data');
  
  console.log('Cleared all AI-related localStorage items');
};

// Helper function to preload Stripe.js script and initialize Stripe
  const preloadStripeScript = (user) => {
  if (document.querySelector('script[src="https://js.stripe.com/v3/"]')) {
    console.log('Stripe script is already loaded');
    // ALWAYS use full_pac keys for PricingPage (new signups)
    // Pass null user to force PAC key usage
    setStripePublicKeyForUser(null);
    return;
  }
  
  console.log('Preloading Stripe.js script and initializing with public key');
  
  // CRITICAL FIX: PricingPage is for NEW signups, so ALWAYS use full_pac keys
  // Pass null user to setStripePublicKeyForUser to force PAC key usage
  setStripePublicKeyForUser(null);
  
  // Initialize Stripe (it will pick up the correct key from the override)
  initializeStripe()
    .then(stripe => {
      console.log('Stripe initialized successfully with full_pac keys');
    })
    .catch(error => {
      console.error('Failed to initialize Stripe:', error);
    });
};


export default function PricingPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const isDark = useDarkMode();
  const [frequency, setFrequency] = useState(pricingData.frequencies[0] || { value: 'monthly', label: 'Monthly' });
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  // Start with pageReady=true to avoid showing the internal spinner unless explicitly turned off
  const [pageReady, setPageReady] = useState(true);
  const [stripeCheckoutUrl, setStripeCheckoutUrl] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isSigningOut, setIsSigningOut] = useState(false);
  const [isDeletingAccount, setIsDeletingAccount] = useState(false);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  
  // Add reference for checkout progress tracking
  const checkoutInProgress = useRef(false);
  
  // Add signOut to the destructured values from useAuth
  const { user, loading, isAdmin, isStripeCheckoutInProgress, initiateStripeCheckout, directFirebaseUser, setUser } = useAuth();
  // Update to include direct Firebase auth
  const isLoggedIn = !!user || !!directFirebaseUser;

  // Check if user is coming from signup
  const justSignedUp = localStorage.getItem('just_signed_up') === 'true';
  const isFromSignup = justSignedUp || location.state?.fromSignup;
  
  // Check if user is coming from settings or create campaign
  const isFromSettings = location.state?.fromSettings || false;
  const isFromCreateTemplate = location.state?.fromCreateTemplate || false;
  const hasHadFreeTrial = user?.hasHadFreeTrial || false;

  // Check if user is coming from a premium feature
  const isFromFeature = location.state?.fromFeature || false;
  const returnPath = location.state?.returnPath || null;
  const featureName = location.state?.featureName || 'previous page';

  // Check if user is coming from settings or create campaign
  const isFromCreateCampaign = location.state?.fromCreateCampaign || false;
  const isFromTemplates = location.state?.fromTemplates || false;

  // CRITICAL: Immediate redirect check for premium users (doesn't wait for pageReady)
  useEffect(() => {
    // Immediate check as soon as user data is available
    if (user && !loading) {
      const isPremiumOrEnterprise = user.planLevel === 'premium' || user.isEnterprise;
      
      if (isPremiumOrEnterprise) {
        // Check if this is a special context where premium users might legitimately be on pricing
        const isFromSettings = location.state?.fromSettings;
        const isFromFeature = location.state?.fromFeature;
        const isFromCreateCampaign = location.state?.fromCreateCampaign;
        const isFromCreateTemplate = location.state?.fromCreateTemplate;
        const isFromTemplates = location.state?.fromTemplates;
        const justSignedUp = localStorage.getItem('just_signed_up') === 'true';
        const comingFromSignup = sessionStorage.getItem('coming_from_signup') === 'true';
        
        // Allow premium users on pricing page only in specific contexts
        const isSpecialContext = isFromSettings || isFromFeature || isFromCreateCampaign || 
                                isFromCreateTemplate || isFromTemplates || justSignedUp || comingFromSignup;
            
        if (!isSpecialContext) {
          console.log("PricingPage: IMMEDIATE Premium user redirect to app", {
            planLevel: user.planLevel,
            contextFlags: {
              isFromSettings,
              isFromFeature, 
              isFromCreateCampaign,
              isFromCreateTemplate,
              isFromTemplates,
              justSignedUp,
              comingFromSignup
            }
          });
          
          // Immediate redirect without waiting for pageReady
          navigate('/app/', { replace: true });
          return;
            }
      }
    }
  }, [user, loading, navigate, location.state]);

  // Simplified page ready logic - no complex auth refresh needed
  useEffect(() => {
    // For most cases, page should be ready immediately
    if (!loading) {
        setPageReady(true);
      }
  }, [loading]);

  // Simplified initialization - remove complex auth handling
  useEffect(() => {
    // Reset any previous error states
    setError('');
            
    // Reset loading state on initial load
    if (isLoading) {
      setIsLoading(false);
    }
    
    // Reset selected plan on initial load
    if (selectedPlan) {
      setSelectedPlan(null);
    }
    
    // Make sure checkout is not in progress
    checkoutInProgress.current = false;
    
    // Page should be ready after minimal delay
    const timer = setTimeout(() => {
      setPageReady(true);
    }, 100);
    
    return () => clearTimeout(timer);
  }, []);

  // Ensure monthly is selected on initial render
  useEffect(() => {
    const defaultFrequency = pricingData.frequencies[0] || { value: 'monthly', label: 'Monthly' };
    console.log('Setting frequency to:', defaultFrequency);
    setFrequency(defaultFrequency);
  }, []);

  // Preload Stripe.js on component mount
  useEffect(() => {
    // Only preload if we're in a browser environment
    if (typeof window !== 'undefined') {
      console.log('Initializing Stripe on component mount');
      preloadStripeScript(user);
    }
  }, [user]); // Add user dependency to re-init when user logs in

  // Check if we're returning from a failed Stripe checkout attempt
  useEffect(() => {
    // Check both possible session ID keys
    const sessionId = localStorage.getItem('stripeSessionId') || localStorage.getItem('stripe_session_id');
    const sessionCreatedAt = localStorage.getItem('stripeSessionCreatedAt');
    
    // Check if we're coming from a legitimate cancellation
    const isComingFromCancel = location.pathname === '/pricing' && 
                              (document.referrer.includes('/cancel') || 
                               sessionStorage.getItem('stripe_checkout_cancelled') === 'true');
    
    // If we're coming from cancel, clear any session data and don't show error
    if (isComingFromCancel) {
      console.log('Coming from checkout cancellation, clearing session data');
      localStorage.removeItem('stripeSessionId');
      localStorage.removeItem('stripe_session_id');
      localStorage.removeItem('stripeSessionCreatedAt');
      localStorage.removeItem('stripe_checkout_initiated');
      sessionStorage.removeItem('stripe_checkout_cancelled');
      return;
    }
    
    // If we have a stored session ID and the page loads, it likely means the redirect failed
    if (sessionId && sessionCreatedAt) {
      const createdAt = parseInt(sessionCreatedAt, 10);
      const now = Date.now();
      const sessionAgeMinutes = (now - createdAt) / (1000 * 60);
      
      // Only consider recent sessions (less than 10 minutes old)
      if (sessionAgeMinutes < 10) {
        // Create a direct URL to the checkout
        const checkoutUrl = `https://checkout.stripe.com/c/pay/${sessionId}`;
        setStripeCheckoutUrl(checkoutUrl);
        
        // Show error with option to try direct URL
        setError('It seems the automatic redirect to the payment page failed. You can try the direct link below or retry the redirect.');
        
        // Additionally, log important debugging information
        console.log('Debug info:');
        console.log('- Session ID:', sessionId);
        console.log('- Session age (minutes):', sessionAgeMinutes);
        console.log('- Stripe public key set:', !!import.meta.env.VITE_STRIPE_PUBLIC_KEY);
        
        // Pre-initialize Stripe again to prepare for manual retry
        preloadStripeScript(user);
      } else {
        // Clear expired session data
        localStorage.removeItem('stripeSessionId');
        localStorage.removeItem('stripe_session_id');
        localStorage.removeItem('stripeSessionCreatedAt');
      }
    }
  }, [location.pathname, user]);

  // Get the current user's plan - updated to use planLevel
  const userPlan = user?.planLevel || 'standard';
  const userPlanInterval = user?.planInterval || null;

  console.log("PricingPage - Current user:", user);
  console.log("PricingPage - Current plan level:", userPlan);
  console.log("PricingPage - Current plan interval:", userPlanInterval);

  // CRITICAL: Redirect premium users away from pricing page
  useEffect(() => {
    // Only redirect if we have user data and are not in a special context
    if (user && !loading && pageReady) {
      const isPremiumOrEnterprise = user.planLevel === 'premium' || user.isEnterprise;
      
      // Check if checkout success is in progress - if so, don't redirect
      if (sessionStorage.getItem('checkout_success_in_progress') === 'true') {
        console.log("PricingPage: Checkout success in progress, skipping redirect");
        return;
      }
      
      // Check if this is a special context where premium users might legitimately be on pricing
      const isFromSettings = location.state?.fromSettings;
      const isFromFeature = location.state?.fromFeature;
      const isFromCreateCampaign = location.state?.fromCreateCampaign;
      const isFromCreateTemplate = location.state?.fromCreateTemplate;
      const isFromTemplates = location.state?.fromTemplates;
      const justSignedUp = localStorage.getItem('just_signed_up') === 'true';
      const comingFromSignup = sessionStorage.getItem('coming_from_signup') === 'true';
      
      // Allow premium users on pricing page only in specific contexts
      const isSpecialContext = isFromSettings || isFromFeature || isFromCreateCampaign || 
                              isFromCreateTemplate || isFromTemplates || justSignedUp || comingFromSignup;
      
      if (isPremiumOrEnterprise && !isSpecialContext) {
        console.log("PricingPage: Premium/Enterprise user detected without special context, redirecting to app", {
          planLevel: user.planLevel,
          isSpecialContext,
          contextFlags: {
            isFromSettings,
            isFromFeature, 
            isFromCreateCampaign,
            isFromCreateTemplate,
            isFromTemplates,
            justSignedUp,
            comingFromSignup
          }
        });
        
        // Redirect to app immediately
        navigate('/app/', { replace: true });
        return;
      }
      
      if (isPremiumOrEnterprise && isSpecialContext) {
        console.log("PricingPage: Premium/Enterprise user in special context, allowing access", {
          planLevel: user.planLevel,
          contextFlags: {
            isFromSettings,
            isFromFeature,
            isFromCreateCampaign,
            isFromCreateTemplate,
            isFromTemplates,
            justSignedUp,
            comingFromSignup
          }
        });
      }
    }
  }, [user, loading, pageReady, navigate, location.state]);

  // Set initial load complete after user data is available
  useEffect(() => {
    // Check if user is coming from sign up
    const comingFromSignup = sessionStorage.getItem('coming_from_signup');
    if (comingFromSignup === 'true') {
      console.log('User is coming from signup');
      // Clear the flag to prevent it from triggering again
      sessionStorage.removeItem('coming_from_signup');
    }

    // Set initial load complete when we have user information
    if (user) {
      setInitialLoadComplete(true);
    }
  }, [user]);

  // Debug log to help troubleshoot visibility issues
  useEffect(() => {
    console.log('PricingPage loaded successfully');
  }, []);

  // Handle signing out
  const handleSignOut = async () => {
    // Set signing out flag for local state and session storage for App.jsx to detect
    setIsSigningOut(true);
    sessionStorage.setItem('isSigningOut', 'true');
    
    // Immediately clear all storage that could cause redirects (except isSigningOut)
    localStorage.removeItem('auth_redirect_state');
    localStorage.removeItem('auth_redirect_destination');
    localStorage.removeItem('selectedPlan');
    localStorage.removeItem('just_signed_up');
    
    // Clear all Stripe-related items that could cause redirect to success page
    localStorage.removeItem('stripeSessionId');
    localStorage.removeItem('stripe_session_id');
    localStorage.removeItem('stripe_checkout_initiated');
    localStorage.removeItem('stripe_checkout_auth_state');
    localStorage.removeItem('stripe_checkout_return_path');
    localStorage.removeItem('post_login_redirect');
    localStorage.removeItem('post_login_session_id');
    localStorage.removeItem('stripe_auth_uid');
    localStorage.removeItem('stripe_auth_email');
    localStorage.removeItem('stripe_auth_timestamp');
    localStorage.removeItem('auth_for_checkout');
    localStorage.removeItem('stripe_auto_login_needed');
    sessionStorage.removeItem('in_stripe_checkout_flow');
    sessionStorage.removeItem('stripe_checkout_cancelled');
    
    try {
      // Clear all AI-related localStorage data
      clearAIStorageData();

      console.log('Signing out user and redirecting to home...');
      
      // Sign out from Firebase directly (bypass AuthContext navigation)
      try {
        await firebaseSignOut(auth);
        console.log('Firebase sign out successful');
      } catch (signOutError) {
        console.error('Error in Firebase sign out:', signOutError);
      }
      
      // Clear user state immediately
      setUser(null);
      
      // Force immediate hard redirect to home page - this will bypass any React Router navigation
      console.log('Performing hard redirect to home page');
      window.location.href = '/';
      
        } catch (error) {
      console.error('Error signing out:', error);
      // Even on error, force hard redirect to home
      console.log('Error occurred, still performing hard redirect to home page');
      window.location.href = '/';
    }
  };

  // Handle deleting account - keep this function as it's used by the header dropdown
  const handleDeleteAccount = async () => {
    // Immediately clear all storage that could cause redirects
    sessionStorage.clear();
    localStorage.removeItem('auth_redirect_state');
    localStorage.removeItem('auth_redirect_destination');
    localStorage.removeItem('selectedPlan');
    localStorage.removeItem('just_signed_up');
    
    // Clear all Stripe-related items that could cause redirect to success page
    localStorage.removeItem('stripeSessionId');
    localStorage.removeItem('stripe_session_id');
    localStorage.removeItem('stripe_checkout_initiated');
    localStorage.removeItem('stripe_checkout_auth_state');
    localStorage.removeItem('stripe_checkout_return_path');
    localStorage.removeItem('post_login_redirect');
    localStorage.removeItem('post_login_session_id');
    localStorage.removeItem('stripe_auth_uid');
    localStorage.removeItem('stripe_auth_email');
    localStorage.removeItem('stripe_auth_timestamp');
    localStorage.removeItem('auth_for_checkout');
    localStorage.removeItem('stripe_auto_login_needed');
    sessionStorage.removeItem('in_stripe_checkout_flow');
    sessionStorage.removeItem('stripe_checkout_cancelled');
    
    // Set deletion flag
    setIsDeletingAccount(true);
    
    try {
      console.log("Starting account deletion and redirect to home...");
      
      // Get the user's ID token
      const idToken = await auth.currentUser.getIdToken();
      
      // Call the delete account API endpoint
      const response = await fetch(`${SERVER_URL}/auth/delete-account`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`
        }
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Failed to delete account (${response.status})`);
      }
      
      // Clear AI-related localStorage data
      clearAIStorageData();
      
      // Sign out from Firebase directly (bypass AuthContext navigation)
      await firebaseSignOut(auth);
      
      // Clear user state immediately
      setUser(null);
      
      // Force immediate hard redirect to home page
      console.log("Account deleted, performing hard redirect to home page");
      window.location.href = '/';
      
    } catch (error) {
      console.error('Error deleting account:', error);
      // Even on error, force hard redirect to home
      console.log('Error occurred during account deletion, still performing hard redirect to home page');
      window.location.href = '/';
    }
  };
      
  // Show AuthLoadingScreen during auth transitions
  if (loading && !user && !isFromSignup && !directFirebaseUser) {
    return <AuthLoadingScreen />;
  }

  // Show AuthLoadingScreen when signing out
  if (isSigningOut) {
    return <AuthLoadingScreen />;
  }

  // DEFENSIVE: If premium/enterprise user somehow gets to render, show loading while redirect happens
  if (user && !loading) {
    const isPremiumOrEnterprise = user.planLevel === 'premium' || user.isEnterprise;
    if (isPremiumOrEnterprise) {
      // Check if this is a special context where premium users might legitimately be here
      const isFromSettings = location.state?.fromSettings;
      const isFromFeature = location.state?.fromFeature;
      const isFromCreateCampaign = location.state?.fromCreateCampaign;
      const isFromCreateTemplate = location.state?.fromCreateTemplate;
      const isFromTemplates = location.state?.fromTemplates;
      const justSignedUp = localStorage.getItem('just_signed_up') === 'true';
      const comingFromSignup = sessionStorage.getItem('coming_from_signup') === 'true';
      
      const isSpecialContext = isFromSettings || isFromFeature || isFromCreateCampaign || 
                              isFromCreateTemplate || isFromTemplates || justSignedUp || comingFromSignup;
      
      if (!isSpecialContext) {
        console.log("PricingPage: DEFENSIVE - Premium user reached render, showing loading");
        return <AuthLoadingScreen />;
      }
    }
  }

  // Define toggleTheme function to switch between light and dark mode
  const toggleTheme = () => {
    // Use the same pattern as LandingHeader - check the hook value directly
    if (isDark) {
      document.documentElement.classList.remove('dark');
      document.body.classList.remove('dark');
      localStorage.setItem('theme', 'light');
        } else {
      document.documentElement.classList.add('dark');
      document.body.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    }
  };

  // Handle selecting a plan - restored Stripe functionality
  const handleSelectPlan = async (tierId, planId) => {
    // FIXED: If user is not authenticated, redirect to signup page
    if (!user) {
      console.log('User not authenticated, redirecting to signup page');
      navigate('/signup', { 
        state: { 
          selectedPlan: { tierId, planId, frequency: frequency.value },
          fromPricing: true 
        } 
      });
        return;
      }
      
    if (isLoading) return;
    
    // Early validation - ensure frequency is properly set
    if (!frequency || !frequency.value) {
      console.error('Frequency not properly initialized:', frequency);
      setError('Page is still loading. Please wait a moment and try again.');
      return;
    }
      
    // Debug logging
    console.log('handleSelectPlan called with:', { tierId, planId });
    console.log('Current frequency state:', frequency);
    console.log('Current user:', { uid: user.uid, planLevel: user.planLevel });
    
    setIsLoading(true);
    setSelectedPlan(tierId);
    setError('');
    
    try {
      // Handle clicks from settings page
      if (isFromSettings) {
        if (userPlan === planId) {
          console.log('Already on this plan, doing nothing');
      setIsLoading(false);
        return;
      }
      
        // For settings page, redirect to billing portal instead of creating new checkout
        console.log('Redirecting to Stripe billing portal from settings...');
        try {
          await redirectToBillingPortal();
          return; // Don't reset loading state as we're redirecting
        } catch (billingError) {
          console.error('Error redirecting to billing portal:', billingError);
          setError('Failed to open billing portal. Please try again.');
          setIsLoading(false);
        return;
      }
      }
      
      // Prevent multiple checkout attempts
      if (checkoutInProgress.current) {
        console.log('Checkout already in progress, ignoring click');
          setIsLoading(false);
          return;
        }
        
      checkoutInProgress.current = true;
      
      console.log('Creating Stripe checkout session for plan:', { tierId, planId, frequency: frequency.value });
      
      // Validate that we have all required data
      if (!planId) {
        throw new Error('Plan ID is required');
      }
      
      if (!frequency || !frequency.value) {
        console.error('Frequency validation failed:', { frequency, frequencyValue: frequency?.value });
        throw new Error('Billing frequency is required. Please refresh the page and try again.');
      }
      
      // Store the selected plan for recovery
      localStorage.setItem('selectedPlan', JSON.stringify({ tierId, planId, frequency: frequency.value }));
      
      // Track Meta Pixel InitiateCheckout event
      const selectedTier = pricingData.tiers.find(tier => tier.id === tierId);
      
      // FIXED: Calculate actual amount in cents based on plan and frequency
      let actualAmountCents = null;
      if (selectedTier?.price?.[frequency.value]) {
        const priceString = selectedTier.price[frequency.value];
        if (priceString !== 'Custom') {
          const priceValue = parseFloat(priceString.replace('$', ''));
          actualAmountCents = Math.round(priceValue * 100); // Convert to cents
        }
      }
      
      trackInitiateCheckout({
        content_name: selectedTier?.name || 'Premium Plan',
        content_category: 'Subscription',
        value: actualAmountCents, // FIXED: Use actual amount in cents or null
        content_ids: [planId],
        content_type: 'product',
        planName: selectedTier?.name || 'Premium',
        planInterval: frequency.value === 'annual' ? 'annual' : 'monthly'
      });
          
      // Get current theme for Stripe checkout
      const currentTheme = document.documentElement.classList.contains('dark') ? 'dark' : 'light';
      
      // Create checkout session with correct parameter format
      const checkoutData = await createCheckoutSession(
        planId,                    // planLevel
        frequency.value,           // interval  
        currentTheme,              // theme
        hasHadFreeTrial ? false : true  // isNewSignup (true if they haven't had free trial)
      );
      
      console.log('Checkout session created:', checkoutData);
      
      // Clear any previous cancellation flags
      sessionStorage.removeItem('stripe_checkout_cancelled');
        
      // Store session info for recovery - use consistent keys
      localStorage.setItem('stripeSessionId', checkoutData.sessionId);
      localStorage.setItem('stripe_session_id', checkoutData.sessionId);
      localStorage.setItem('stripeSessionCreatedAt', Date.now().toString());
      
      // Store auth state for recovery after Stripe redirect
      const authState = {
        uid: user.uid,
        email: user.email,
        planLevel: user.planLevel,
        timestamp: Date.now()
      };
      localStorage.setItem('stripe_checkout_auth_state', JSON.stringify(authState));
            
      // Mark that we're initiating checkout
      localStorage.setItem('stripe_checkout_initiated', 'true');
      
      // Redirect to Stripe checkout
            console.log('Redirecting to Stripe checkout...');
      await redirectToCheckout(checkoutData.sessionId);
            
      // If we get here, the redirect failed
      console.error('Stripe redirect failed - should not reach this point');
      setError('Failed to redirect to payment page. Please try again.');
      checkoutInProgress.current = false;
      setIsLoading(false);
      
    } catch (err) {
      console.error('Error handling plan selection:', err);
            
      // Reset checkout progress
      checkoutInProgress.current = false;
      
      // Handle specific error types
      if (err.message?.includes('Stripe not initialized')) {
        setError('Payment system is loading. Please wait a moment and try again.');
      } else if (err.message?.includes('network') || err.message?.includes('fetch')) {
        setError('Network error. Please check your connection and try again.');
      } else {
        setError(err.message || 'Failed to process your request. Please try again.');
            }
            
            setIsLoading(false);
          }
  };

  // Handle manual retry of Stripe redirect - restored functionality
  const handleManualRedirect = async () => {
    if (!stripeCheckoutUrl) {
      setError('No checkout URL available. Please try selecting a plan again.');
      return;
    }
    
    setIsLoading(true);
    setError('');
    
    try {
      console.log('Attempting manual redirect to Stripe checkout...');
      
      // Extract session ID from the stored URL
      const sessionId = localStorage.getItem('stripeSessionId') || localStorage.getItem('stripe_session_id');
      
      if (sessionId) {
        // Try using the redirectToCheckout function first
        await redirectToCheckout(sessionId);
      } else {
        // Fallback to direct window redirect
        console.log('Using direct window redirect as fallback');
        window.location.href = stripeCheckoutUrl;
      }
      
    } catch (error) {
      console.error('Manual redirect failed:', error);
      setError('Manual redirect failed. Please try opening the checkout in a new tab.');
      setIsLoading(false);
  }
  };

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900 flex flex-col">
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute left-1/2 top-0 -z-10 -translate-x-1/2 blur-3xl xl:-top-6" aria-hidden="true">
          <div
            className="aspect-[1155/678] w-[72.1875rem] bg-gradient-to-tr from-purple-200 to-indigo-300 opacity-20 dark:from-purple-800 dark:to-indigo-900 dark:opacity-30"
            style={{
              clipPath:
                'polygon(74.1% 44.1%, 100% 61.6%, 97.5% 26.9%, 85.5% 0.1%, 80.7% 2%, 72.5% 32.5%, 60.2% 62.4%, 52.4% 68.1%, 47.5% 58.3%, 45.2% 34.5%, 27.5% 76.7%, 0.1% 64.9%, 17.9% 100%, 27.6% 76.8%, 76.1% 97.7%, 74.1% 44.1%)',
            }}
          />
        </div>
      </div>

      {/* Simple Landing Header with Sign Out Button */}
      <SimpleLandingHeader 
        isDark={isDark} 
        toggleTheme={toggleTheme}
        backButtonText={user ? (isSigningOut ? "Signing Out..." : "Sign Out") : "Back to Home"}
        onBackClick={user ? (isSigningOut ? null : handleSignOut) : () => navigate('/')}
        demoTemplatesLink={null}
        showDelete={!!user}
        onDeleteClick={user ? () => setShowDeleteConfirm(true) : null}
        showLogo={true}
      />

      {/* Main content */}
      <div className="flex-1 mx-auto max-w-7xl px-6 lg:px-8 flex flex-col items-center py-16">
        <div className="mx-auto max-w-3xl sm:max-w-4xl md:mx-auto md:text-center xl:max-w-5xl mb-4">
          <h2 className="text-base font-semibold leading-7 text-purple-600 dark:text-purple-400 text-center">Pricing</h2>
          <p className="mt-2 text-4xl font-semibold tracking-tight leading-tight text-balance text-gray-900 dark:text-white sm:text-5xl sm:leading-tight mx-auto text-center max-w-[90%]">
            Simple, transparent pricing for everyone
          </p>
          <p className="mt-6 text-lg text-gray-600 dark:text-gray-400 mx-auto text-center max-w-[90%]">
            Start with a free trial and cancel anytime - no long-term commitments required.
          </p>

          <p className="mt-3 text-sm text-gray-500 dark:text-gray-400 opacity-50 dark:opacity-50 mx-auto text-center max-w-[90%]">
            Advocacy Lab is now part of RoboCent / GOTV.com.
          </p>

        </div>
        
        {/* Error message - keep it from the original */}
        {error && (
          <div className="mt-6 mx-auto max-w-md rounded-md bg-red-50 dark:bg-red-900/50 p-4">
            <div className="flex">
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800 dark:text-red-200">Error</h3>
                <div className="mt-2 text-sm text-red-700 dark:text-red-300">
                  {error}
                </div>
                

                
                {/* Show manual sign out option if there was an error signing out */}
                {error && error.includes('sign out') && (
                  <div className="mt-4">
                    <button
                      onClick={() => window.location.href = '/'}
                      className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                    >
                      Force Return to Home
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
        
        {/* Success message - keep it from the original */}
        {successMessage && (
          <div className="mt-6 mb-4 mx-auto max-w-md rounded-md bg-green-50 dark:bg-green-900/50 p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <Check className="h-5 w-5 text-green-500 dark:text-green-400" />
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-green-800 dark:text-green-200">Success</h3>
                <div className="mt-2 text-sm text-green-700 dark:text-green-300">
                  {successMessage}
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* Show loading indicator when page is not ready */}
        {!pageReady ? (
          <div className="my-16 flex flex-col items-center justify-center">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-purple-600 dark:border-purple-400 mb-4"></div>
            <p className="text-gray-600 dark:text-gray-400">Loading subscription information...</p>
          </div>
        ) : (
          <>
            <div className="mt-4">
              <FrequencyToggle 
                frequency={frequency} 
                setFrequency={setFrequency} 
                isLoading={isLoading} 
                frequencies={pricingData.frequencies} 
              />
            </div>
            
            <PricingPlans 
              pricing={{
                ...pricingData,
                tiers: pricingData.tiers.filter(tier => tier.planId === 'premium')
              }} 
              frequency={frequency} 
              isLoading={isLoading}
              selectedPlan={selectedPlan} 
              handleSelectPlan={handleSelectPlan} 
              isFromSettings={isFromSettings}
              isFromSignup={isFromSignup}
              isFromCreateTemplate={isFromCreateTemplate}
              userPlan={userPlan}
              hasHadFreeTrial={hasHadFreeTrial}
              isLoggedIn={isLoggedIn}
            >
              <RoboCentSubscriptionsCard isAnnual={frequency?.value === 'annual'} />
            </PricingPlans>

            {/* Support CTA below the two boxes */}
            <div className="mt-10 pt-2 w-full flex justify-center">
              {(() => {
                const calendlyUrl = pricingData.tiers.find(t => t.contactInfo?.calendlyUrl)?.contactInfo?.calendlyUrl || kevinMeetingUrl;
                return (
                  <p className="text-sm text-gray-600 dark:text-gray-300">
                    Have questions?{' '}
                    <a
                      href={calendlyUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-semibold text-purple-700 hover:text-purple-800 dark:text-purple-300 dark:hover:text-purple-200"
                    >
                      Book a call with our team
                    </a>{' '}
                    to learn more.
                  </p>
                );
              })()}
            </div>
          </>
        )}
        
        {/* Delete account confirmation modal - Keep this as it's used by the header dropdown */}
        {showDeleteConfirm && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-0 bg-black bg-opacity-50">
            <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full sm:my-8">
              <div className="flex items-center justify-center text-red-500 mb-4">
                <AlertTriangle className="h-12 w-12" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white text-center mb-2">
                Delete your account?
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 text-center mb-6">
                This action cannot be undone. All your data will be permanently deleted.
              </p>
              <div className="flex justify-center gap-4">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteAccount}
                  disabled={isDeletingAccount}
                  className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-red-600 hover:bg-red-700 dark:bg-red-700 dark:hover:bg-red-600 disabled:opacity-70"
                >
                  {isDeletingAccount ? (
                    <div className="flex items-center">
                      <ButtonSpinner color="white" />
                      <span className="ml-2">Deleting...</span>
                    </div>
                  ) : (
                    "Delete account"
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* Footer with extra padding */}
      <Footer />
      <div className="pb-16"></div>
    </div>
  );
}